import {isNumber} from '#lit/utils/index.js';

export type PointTuple = [IPoint['x'], IPoint['y']];

export function isPointTuple(value: unknown): value is PointTuple {
  return Array.isArray(value) && value.length === 2 && value.every(isNumber);
}

export interface IPoint {
  x: number;
  y: number;

  set(x: number, y: number): IPoint;

  // Adds a vector to the current vector
  add(point: IPoint): IPoint;
  // Subtracts a vector to the current vector
  subtract(point: IPoint): IPoint;
  // Divide a point by a point.
  divide(point: IPoint): IPoint;
  // multiply a point by  point
  multiply(point: IPoint): IPoint;
  // To use with other APIs that except a tuple representation.
  asTuple(): PointTuple;
  /**
   * Returns a point evaluating to the delta between the current point
   * and other provided as an argument.
   */
  diff(point: IPoint): IPoint;
  /**
   * Compares 2 objects and define if they are equivalent as ValueObjects.
   */
  equals(point: IPoint): boolean;
}

export class Point implements IPoint {
  protected constructor(
    public x: number,
    public y: number
  ) {
    this.x = x;
    this.y = y;
  }

  static from(x: number, y?: number): Point {
    return new Point(x, y ?? x);
  }

  static fromTuple(tuple: [number, number]): Point {
    const [x, y] = tuple;
    return new Point(x, y);
  }

  set(x: number, y: number): IPoint {
    this.x = x;
    this.y = y;

    return this;
  }

  add(point: IPoint): IPoint {
    this.x += point.x;
    this.y += point.y;

    return this;
  }

  subtract(point: IPoint): IPoint {
    this.x -= point.x;
    this.y -= point.y;

    return this;
  }

  divide(point: IPoint): IPoint {
    this.x = this.x / point.x;
    this.y = this.y / point.y;

    return this;
  }

  multiply(point: IPoint): IPoint {
    this.x = this.x * point.x;
    this.y = this.y * point.y;

    return this;
  }

  diff(point: IPoint): IPoint {
    return Point.from(point.x - this.x, point.y - this.y);
  }

  asTuple(): [number, number] {
    return [this.x, this.y];
  }

  equals(point: IPoint): boolean {
    return this.x === point.x && this.y === point.y;
  }
}
